import depsloader from "../../../depsloader";

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.autocompleteRequest = null;
    this.reverseRequest = null;
    this.autocompleteGeneration = 0;
    this.reverseGeneration = 0;
  }

  cancelAutocomplete() {
    this.autocompleteGeneration += 1;
    if (this.autocompleteRequest) {
      this.autocompleteRequest.source.cancel();
      this.autocompleteRequest = null;
    }
  }

  cancelReverse() {
    this.reverseGeneration += 1;
    if (this.reverseRequest) {
      this.reverseRequest.source.cancel();
      this.reverseRequest = null;
    }
  }

  destroy() {
    this.cancelAutocomplete();
    this.cancelReverse();
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    const geoaddress = {};

    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const properties = l.properties;
    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (typeof lat !== "number" || typeof lng !== "number"
      || !Number.isFinite(lat) || !Number.isFinite(lng)
      || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const type = properties.type;
    const name = properties.name || "";

    if (type == "city") {
      geoaddress["city"] = name;
    } else if (type == "district") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = name || properties.district || "";
    } else if (type == "house") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = properties.street || "";
      geoaddress["number"] = properties.housenumber || "";
    } else if (type == "street") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = name;
    } else if (type == "locality") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = properties.street || "";
    } else if (properties.osm_key == "place"
      && ["city", "town", "village", "municipality"].includes(properties.osm_value || "")) {
      geoaddress["city"] = name;
    } else if ((properties.osm_key == "place" && properties.osm_value == "suburb")
      || properties.osm_key == "boundary") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = name;
    } else if (properties.osm_key == "highway") {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = name;
    } else {
      geoaddress["city"] = properties.city || "";
      geoaddress["district"] = properties.district || "";
      geoaddress["street"] = properties.street || "";
    }

    if (properties.postcode) {
      geoaddress["zip"] = properties.postcode;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = properties.state;
    geoaddress["country"] = properties.country;

    geoaddress["text"] = this._geoaddressText(geoaddress);
    if (!geoaddress["city"] && !geoaddress["street"] && !geoaddress["district"] && name) {
      geoaddress["text"] = [name, geoaddress["state"], geoaddress["country"]]
        .filter(Boolean)
        .join(", ");
    }

    return geoaddress;
  }

  geocode(params) {
    const runtime = window.ScreenworkMapsRuntime || {};
    if (!runtime.geocoderUrl || !runtime.geocoderKey) {
      return Promise.resolve([]);
    }

    this.cancelAutocomplete();
    const generation = this.autocompleteGeneration;
    const source = this.axios.CancelToken.source();
    const request = { source };
    this.autocompleteRequest = request;
    params.set("key", runtime.geocoderKey);

    return this.axios.get(runtime.geocoderUrl, {
      cancelToken: source.token,
      params,
      timeout: 25000
    }).then(res => {
      if (generation !== this.autocompleteGeneration
        || this.autocompleteRequest !== request) {
        return [];
      }
      const features = res.data && Array.isArray(res.data.features)
        ? res.data.features
        : [];
      return features.map(l => this._geoaddress(l)).filter(Boolean);
    }).catch((error) => {
      if (this.axios.isCancel(error)) {
        return [];
      }
      throw error;
    }).finally(() => {
      if (this.autocompleteRequest === request) {
        this.autocompleteRequest = null;
      }
    });
  }

  autocomplete(q) {
    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    //params.append("osm_tag", "!boundary:protected_area");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    const runtime = window.ScreenworkMapsRuntime || {};
    if (!runtime.reverseGeocoderUrl || !runtime.geocoderKey
      || typeof lat !== "number" || typeof lng !== "number"
      || !Number.isFinite(lat) || !Number.isFinite(lng)
      || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return Promise.resolve(null);
    }

    this.cancelReverse();
    const generation = this.reverseGeneration;
    const source = this.axios.CancelToken.source();
    const request = { source };
    this.reverseRequest = request;

    return this.axios.get(runtime.reverseGeocoderUrl, {
      cancelToken: source.token,
      params: {
        lat,
        lon: lng,
        lang: "de",
        key: runtime.geocoderKey
      },
      timeout: 25000
    }).then(res => {
      if (generation !== this.reverseGeneration || this.reverseRequest !== request) {
        return null;
      }
      const features = res.data && Array.isArray(res.data.features)
        ? res.data.features
        : [];
      return features.length ? this._geoaddress(features[0]) : null;
    }).catch(() => {
      return null;
    }).finally(() => {
      if (this.reverseRequest === request) {
        this.reverseRequest = null;
      }
    });
  }
}

function GeocoderFactory() {
  return depsloader.load("axios").then(axios => new Geocoder(axios));
}

export { GeocoderFactory };

export default {};
